/*
  La Sura - save and quick add on product cards (blocks/lasura-card-quick.liquid).

  Save: the heart keeps a shortlist in this browser (localStorage "lasura:shortlist"), shared
  by every card. The tray, list and sharing load from lasura-shortlist.js when needed.

  Quick add: the Add button opens the card's <dialog>. A size copies its variant into the
  form's hidden id and updates both prices (under the title and in the Add button); with a
  headcount from the party planner it starts on the smallest size that feeds everyone. The
  message field counts its characters. On phones the sheet's head drags down to close it. Adding goes through Horizon's product-form-component
  (drawer, count, fly-to-basket); the sheet closes on cart:update. Clicks in the sheet stop
  there, so the card never treats them as a click through to the product.

  Events (for pixels, as the cake builder does): shortlist_save, shortlist_remove,
  quick_add_open, quick_add_add. Each is a lasura:<name> event and Shopify.analytics.publish.
*/
import { say as announce } from '@theme/lasura-basket-utils';

const KEY = 'lasura:shortlist';
let memory = [];

const read = () => {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return memory;
  }
};
const write = (list) => {
  memory = list;
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {}
  document.dispatchEvent(new CustomEvent('lasura:shortlist', { detail: list }));
};
const track = (name, detail = {}) => {
  document.dispatchEvent(new CustomEvent(`lasura:${name}`, { detail }));
  try {
    window.Shopify?.analytics?.publish?.(name, detail);
  } catch {}
};
/* The shortlist module, once: when something is saved, a shared link is being opened, or a
   signed-in shopper has cakes in their account (the tray then links to the account page). */
let shortlist = null;
const loadShortlist = (card) =>
  (shortlist ||= import(card.dataset.shortlistJs).then((m) => (m.init(card, { read, write, track, announce }), m)));

/* The close button takes focus as a sheet opens. Opened by a tap, it shows no ring (Safari drew
   one) until the keyboard moves it; opened from the keyboard, it does. A tap in the gallery opens
   the sheet by a script click, so the last input is kept rather than the click's own. */
let tapped = false;
addEventListener('pointerdown', () => (tapped = true), true);
addEventListener('keydown', () => (tapped = false), true);

document.addEventListener('lasura:shortlist', () => {
  for (const card of document.querySelectorAll('lasura-card-quick')) card.syncSaved();
});

class LasuraCardQuick extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    const q = new URLSearchParams(location.search);
    if (read().length || q.has('shortlist') || q.has('cake') || +this.dataset.accountCount > 0) loadShortlist(this);

    this.save = this.querySelector('[data-lasura-heart]');
    this.save?.addEventListener('click', this.#toggleSave);
    this.syncSaved();

    this.dialog = this.querySelector('.lasura-quick__sheet');
    this.querySelector('[data-lasura-quick-open]')?.addEventListener('click', this.#open);
    if (this.dialog) {
      this.dialog.addEventListener('click', (event) => {
        event.stopPropagation();
        if (event.target === this.dialog) this.dialog.close();
      });
      this.closer = this.querySelector('[data-lasura-quick-close]');
      this.closer?.addEventListener('click', () => this.dialog.close());
      this.closer?.addEventListener('blur', () => this.closer.removeAttribute('data-by-pointer'));
      this.dialog.addEventListener('change', this.#onSize);
      this.message = this.dialog.querySelector('[data-lasura-quick-message]');
      this.message?.addEventListener('input', this.#onMessage);
      this.panel = this.dialog.querySelector('.lasura-quick__panel');
      this.dialog.querySelector('.lasura-quick__body')?.addEventListener('scroll', this.#onScroll, { passive: true });
      this.#dragToClose(this.dialog.querySelector('[data-lasura-quick-drag]'));
    }
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.#onCart);
  }

  syncSaved() {
    /* Designed cakes (kind: design) are saved from the builder, not by this heart. */
    this.save?.setAttribute('aria-pressed', String(read().some((x) => x.kind !== 'design' && x.handle === this.dataset.handle)));
  }

  #toggleSave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const list = read();
    const i = list.findIndex((x) => x.kind !== 'design' && x.handle === this.dataset.handle);
    if (i >= 0) {
      list.splice(i, 1);
      announce(this.dataset.removedText);
      track('shortlist_remove', { product: this.dataset.handle, count: list.length });
    } else {
      const { handle, title, url, image } = this.dataset;
      list.push({ handle, title, url, image });
      announce(this.dataset.savedText);
      track('shortlist_save', { product: handle, count: list.length });
      if (document.body.classList.contains('lasura-motion') && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.save.classList.remove('is-popping');
        void this.save.offsetWidth;
        this.save.classList.add('is-popping');
      }
    }
    loadShortlist(this).then(() => write(list));
  };

  #open = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!this.dialog) return;
    document.addEventListener('cart:update', this.#onCart);
    this.#sizeForParty();
    this.closer?.toggleAttribute('data-by-pointer', tapped);
    this.dialog.showModal();
    this.dialog.querySelector('lasura-search-inspiration')?.play?.();
    track('quick_add_open', { product: this.dataset.handle });
  };

  /* The basket changed while the sheet was open: the add went through. */
  #onCart = () => {
    document.removeEventListener('cart:update', this.#onCart);
    if (!this.dialog?.open) return;
    track('quick_add_add', { product: this.dataset.handle, variant: this.dialog.querySelector('[data-lasura-quick-variant]')?.value });
    this.dialog.close();
  };

  #sizeForParty() {
    let guests = 0;
    try {
      guests = Number(JSON.parse(localStorage.getItem('lasura:party') || '{}').guests) || 0;
    } catch {}
    if (!guests) return;
    const radios = [...this.dialog.querySelectorAll('input[type="radio"][data-serves-max]:not(:disabled)')];
    const fit = radios.find((r) => Number(r.dataset.servesMax) >= guests) || radios[radios.length - 1];
    if (!fit || fit.checked) return;
    fit.checked = true;
    fit.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #onSize = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    const hidden = this.dialog.querySelector('[data-lasura-quick-variant]');
    if (hidden) hidden.value = input.value;
    if (!input.dataset.price) return;
    for (const el of this.dialog.querySelectorAll('[data-lasura-quick-price], [data-lasura-quick-cta-price]')) {
      el.textContent = input.dataset.price;
    }
  };

  #onMessage = () => {
    const count = this.dialog.querySelector('[data-lasura-quick-count]');
    if (count) count.textContent = `${this.message.value.length}/${this.message.maxLength}`;
  };

  #onScroll = (event) => {
    this.panel?.classList.toggle('is-scrolled', event.currentTarget.scrollTop > 4);
  };

  /* Phones only (the handle shows below 750px): drag the head down past a quarter of the sheet,
     or flick it, to close. Anything less springs back. */
  #dragToClose(handle) {
    if (!handle) return;
    const phone = matchMedia('(max-width: 749px)');
    let startY = 0;
    let startT = 0;
    let dy = 0;
    let id = null;
    const settle = (close) => {
      const sheet = this.dialog;
      sheet.style.transition = matchMedia('(prefers-reduced-motion: reduce)').matches ? '' : 'transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)';
      sheet.style.transform = close ? 'translateY(100%)' : '';
      const done = () => {
        sheet.style.transition = '';
        if (close) {
          sheet.close();
          sheet.style.transform = '';
        }
      };
      if (sheet.style.transition) sheet.addEventListener('transitionend', done, { once: true });
      else done();
    };
    handle.addEventListener('pointerdown', (event) => {
      if (!phone.matches || event.button !== 0 || event.target.closest('a, button')) return;
      id = event.pointerId;
      startY = event.clientY;
      startT = event.timeStamp;
      dy = 0;
      handle.setPointerCapture(id);
    });
    handle.addEventListener('pointermove', (event) => {
      if (event.pointerId !== id) return;
      dy = Math.max(0, event.clientY - startY);
      this.dialog.style.transform = dy ? `translateY(${dy}px)` : '';
    });
    const end = (event) => {
      if (event.pointerId !== id) return;
      id = null;
      const speed = dy / Math.max(1, event.timeStamp - startT);
      settle(dy > this.dialog.offsetHeight / 4 || (dy > 40 && speed > 0.6));
    };
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  }
}

if (!customElements.get('lasura-card-quick')) customElements.define('lasura-card-quick', LasuraCardQuick);
